import React, { useMemo } from 'react';
import Section from '../../../Components/Containers/Section';
import Chart, { pickColour } from '../../../Components/Chart';
import { usePreference } from '../../../Data/Preference';
import ErrorBoundary from '../../../Components/Containers/ErrorBoundary/ErrorBoundary';
import { formatTooltipLabel, tickCallback, formatTime } from './helpers';
import Money from '../../../Components/Formatting/Money';
import NoData from './NoData';

const BarPieTableChart = ({title, subtitle, groupingLabel, dataLabel, labelValueData, noLabel = '[None]', sort = 'desc', dataType, defaultType = 'Bar'}) => {
    const [type, setType] = usePreference('report:type:'+title, defaultType);

    const marshalledData = useMemo(() => {
        if (sort.toLowerCase() === 'asc') {
            labelValueData.sort((a, b) => a.value - b.value);
        } else if (sort.toLowerCase() === 'desc') {
            labelValueData.sort((a, b) => b.value - a.value);
        }
        return {
            labels: labelValueData.map(d => {
                if (!d.label) {
                    return noLabel
                };
                
                return type === 'Bar' ? d.label.split(' ').slice(0, 2) : `${d.label}`
            }),
            datasets: [{
                label: dataLabel,
                data: labelValueData.map(d => d.value),
                backgroundColor: labelValueData.map((d, i) => pickColour(i, labelValueData.length)),
            }]
        }
    }, [sort, noLabel, labelValueData, dataLabel, type])

    if (labelValueData.length === 0 || labelValueData.every(d => d.value === 0)) {
        return <NoData title={title} subtitle={subtitle} />
    }
    
    return <Section title={title}
        subtitle={subtitle}
        options={['Bar', 'Pie', 'Table']} optionsVal={type} optionsCallback={setType}
    >
        <ErrorBoundary>
            {type !== 'Table' && <Chart type={type}
                size="max-h-[50vh] w-auto"
                data={marshalledData}
                options={type === 'Bar' ? getBarOptions(dataType) : getPieOptions(dataType)}
            />}
            {type === 'Table' && <Table {...{groupingLabel, dataLabel, labelValueData, noLabel, dataType}} />}
        </ErrorBoundary>
    </Section>
}

const Table = ({groupingLabel, dataLabel, labelValueData, noLabel, dataType}) => {

    const dataFormatter = (val) => {
        if (dataType === 'currency') {
            return <Money pence={val} />;
        }
        if (dataType === 'time') {
            return formatTime(val);
        }

        return val;
    }

    return <table className="w-full table-auto divide-y">
        <thead>
            <tr className="text-sm font-bold">
                <th className="text-left p-2">{groupingLabel}</th>
                <th className="text-center p-2">{dataLabel}</th>
            </tr>
        </thead>
        <tbody>
            {labelValueData.map((d, i) => <tr key={i} className="even:bg-slate-50">
                <td className="text-left p-2 rounded-l-md">{d.label ?? noLabel}</td>
                <td className="text-center p-2 rounded-r-md">{dataFormatter(d.value)}</td>
            </tr>)}
        </tbody>
    </table>

}

const getBarOptions = (dataType) => {
    return {
        indexAxis: 'x',
        plugins: {
            tooltip: {
                callbacks: {
                    title: (context) => context[0].label.replaceAll(',', ' '),
                    label: (tooltipItem, data) => formatTooltipLabel(tooltipItem, data, dataType),
                }
            },
            legend: {
                display: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                stepSize: 1,
                precision: 0,
                ticks: {
                    callback: (value, index, values) => tickCallback(value, index, values, dataType),
                }
            },
        },
    };
}

const getPieOptions = (dataType) => {
    return {
        cutout: '25%',
        indexAxis: 'x',
        plugins: {
            tooltip: {
                callbacks: {
                    label: (tooltipItem, data) => formatTooltipLabel(tooltipItem, data, dataType),
                }
            }
        }
    };
}



export default BarPieTableChart;
